import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { Marketplace, Targeting } from "@front/m19-api-client";
import { IButtonDirective, MODAL_DATA, ModalRef, SpinnerComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { KeywordListManagerComponent } from "@m19-board/shared/keyword-list-manager/keyword-list-manager.component";
export interface StrategyKeywordModalData {
  marketplace: Marketplace;
  keywords: Targeting[];
  isReadOnly: boolean;
  loading?: boolean;
}

export enum StrategyKeywordModalResult {
  ADDED_KEYWORDS,
  DELETED_KEYWORDS,
}

export interface StrategyKeywordModalOutput {
  keywordsToAdd: Targeting[];
  keywordsToDelete: Targeting[];
}

@Component({
  selector: "app-strategy-keyword-modal",
  template: ` <div class="flex h-full flex-col">
    <div class="flex min-h-0 flex-1 flex-col gap-4 p-3">
      <ISpinner [display]="!!modalData.loading" class="absolute top-48 flex w-full items-center justify-center" />
      <app-keyword-list-manager
        class="min-h-0 flex-[1_1_0]"
        [initTargetings]="modalData.keywords"
        [isReadOnly]="modalData.isReadOnly"
        (targetingsChanged)="onKeywordsChanged($event)"
      />
    </div>
    <div class="flex shrink-0 justify-end gap-2 p-3" *transloco="let t">
      <button iButton variant="secondary" size="md" (click)="modalRef.close()">{{ t("common.close") }}</button>
      <button iButton size="md" [disabled]="isSaveDisabled()" (click)="onSave()">{{ t("common.save") }}</button>
    </div>
  </div>`,
  imports: [SpinnerComponent, TranslocoDirective, IButtonDirective, KeywordListManagerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyKeywordModalComponent {
  protected readonly modalData = inject(MODAL_DATA) as StrategyKeywordModalData;
  readonly modalRef = inject(ModalRef);
  readonly StrategyKeywordModalResult = StrategyKeywordModalResult;

  toAddKeywords = signal<Targeting[]>([]);
  toDeleteKeywords = signal<Targeting[]>([]);

  readonly isSaveDisabled = computed(
    () => this.modalData.isReadOnly || (this.toAddKeywords().length === 0 && this.toDeleteKeywords().length === 0),
  );

  onKeywordsChanged(keywords: Targeting[]) {
    const toAdd = keywords.filter((k) => !this.modalData.keywords.includes(k));
    const toDelete = this.modalData.keywords.filter((k) => !keywords.includes(k));
    if (toAdd.length > 0) {
      this.toAddKeywords.set(toAdd);
    }
    if (toDelete.length > 0) {
      this.toDeleteKeywords.set(toDelete);
    }
  }

  onSave() {
    this.modalRef.close({ keywordsToAdd: this.toAddKeywords(), keywordsToDelete: this.toDeleteKeywords() });
  }
}
